/*
PURPOSE: HexBloom — the refined hex-packed coral bloom for the public editorial
pages. A deterministic honeycomb cluster of soft radial-gradient discs in a
warm coral/peach palette, with a distance-based pale->deep colour gradient.
USE CASES:
- Decorative hero accent on Home2, and the recent-coral / ecosystem bloom
  visuals on Home2 and ForArtists.
DOMAIN BOUNDARIES:
- Presentational only; no data fetching. Optionally data-seeded: when a `coral`
  is passed, the layout seed derives from coral.transactionId and the cluster
  size (rings) from the artist/genre count, so the bloom is a stable, ownable
  mark per coral — not a literal per-artist chart. Explicit seed/rings props
  always override.
- Transparent by default (no background) so it blends into the host section;
  pass `bgColor` for the standalone cream-paper backdrop.
- Self-contained. The canonical bloom component on the public pages.
*/

import React, { useMemo } from 'react';
import type { CoralData } from '../CoralVisualisation/CoralVisualisation';

export interface BloomPalette {
  pale: string;
  light: string;
  mid: string;
  deep: string;
  deeper: string;
}

export type BloomShape = 'circle' | 'hex';

export interface HexBloomProps {
  /** Optional coral data — when present, seeds the layout (transactionId) and
   *  sizes the cluster (artist count), unless seed/rings are given. */
  coral?: CoralData;
  /** Explicit layout seed. Overrides the coral-derived seed. */
  seed?: number;
  /** Bounding square in px. */
  size?: number;
  /** Hex rings around the centre. Overrides the coral-derived size. */
  rings?: number;
  /** Base disc radius as a fraction of the canvas. */
  radius?: number;
  /** Wrapper blur in px. */
  blur?: number;
  /** 0..1 multiplier on every disc's alpha. */
  opacityScale?: number;
  /** Disc shape. */
  shape?: BloomShape;
  /** 0..1 — how strongly the hex shape is applied (0 = circle, 1 = full hex). */
  shapeMix?: number;
  /** Five-stop coral/peach palette. */
  palette?: BloomPalette;
  /** Optional opaque backdrop (cream "paper"). Omit for a transparent bloom. */
  bgColor?: string;
  className?: string;
}

const DEFAULT_PALETTE: BloomPalette = {
  pale: '#fde2cf',
  light: '#f8c9ad',
  mid: '#f1a589',
  deep: '#e8856a',
  deeper: '#dc6a52',
};

// Tuned production defaults (the standalone harness's TWEAK_DEFAULS).
const DEFAULT_SIZE = 360;
const DEFAULT_SEED = 4;
const DEFAULT_RINGS = 2;
const DEFAULT_RADIUS = 0.11;
const DEFAULT_BLUR = 1;
const DEFAULT_OPACITY_SCALE = 0.52;
const DEFAULT_SHAPE: BloomShape = 'hex';
const DEFAULT_SHAPE_MIX = 0.5;

/** FNV-1a string -> 32-bit unsigned int, for a stable numeric seed. */
function hashStringToSeed(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Cluster size (hex rings) bucketed from the number of artists/genres. */
function ringsForArtistCount(count: number): number {
  if (count <= 4) return 1;
  if (count <= 12) return 2;
  return 3;
}

/** Convert "#rrggbb" to "rgba(r,g,b,a)". */
function hexWithAlpha(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

interface BloomCircle {
  px: number;
  py: number;
  rad: number;
  color: string;
  opacity: number;
  hexDist: number;
}

/**
 * A hex-packed cluster of soft-edged coral/peach discs. Each disc is a feathered
 * radial gradient; discs sit in honeycomb packing with a few smaller infill
 * discs tucked between to break up the regular grid.
 */
const HexBloom: React.FC<HexBloomProps> = ({
  coral,
  seed,
  size = DEFAULT_SIZE,
  rings,
  radius = DEFAULT_RADIUS,
  blur = DEFAULT_BLUR,
  opacityScale = DEFAULT_OPACITY_SCALE,
  shape = DEFAULT_SHAPE,
  shapeMix = DEFAULT_SHAPE_MIX,
  palette = DEFAULT_PALETTE,
  bgColor,
  className,
}) => {
  // Data-seeded: derive the seed + cluster size from the coral when the caller
  // does not pass them explicitly. Explicit props always win.
  const resolvedSeed =
    seed ?? (coral ? hashStringToSeed(coral.transactionId) : DEFAULT_SEED);
  const resolvedRings =
    rings ?? (coral ? ringsForArtistCount(coral.artists.length) : DEFAULT_RINGS);

  // Seeded PRNG (mulberry32) so layouts are stable per resolved seed.
  const rng = useMemo(() => {
    let t = (resolvedSeed >>> 0) || 1;
    return () => {
      t += 0x6d2b79f5;
      let r = t;
      r = Math.imul(r ^ (r >>> 15), r | 1);
      r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }, [resolvedSeed]);

  const circles = useMemo<BloomCircle[]>(() => {
    const cx = 0.5;
    const cy = 0.55;
    const r0 = radius;
    // hex step distances < 2*r0 so neighbours overlap (no gaps between discs)
    const dx = r0 * 1.55;
    const dy = r0 * 1.34;

    const cells: BloomCircle[] = [];
    // axial hex coords
    for (let q = -resolvedRings; q <= resolvedRings; q++) {
      for (let rr = -resolvedRings; rr <= resolvedRings; rr++) {
        const s = -q - rr;
        if (Math.max(Math.abs(q), Math.abs(rr), Math.abs(s)) > resolvedRings) continue;
        const hexDist = (Math.abs(q) + Math.abs(rr) + Math.abs(s)) / 2;
        // Drop a couple of outermost cells for an irregular silhouette, but
        // keep most of the cluster filled (no internal gaps).
        const keepProb = hexDist === 0 ? 1 : hexDist === 1 ? 1 : 0.78;
        if (rng() > keepProb) continue;

        const px = cx + (q + rr / 2) * dx + (rng() - 0.5) * r0 * 0.08;
        const py = cy + rr * dy + (rng() - 0.5) * r0 * 0.08;

        // radius: outer-ring discs a touch smaller, with mild jitter
        const distNorm = resolvedRings === 0 ? 0 : hexDist / resolvedRings;
        const rad = r0 * (1.0 - distNorm * 0.12) * (0.92 + rng() * 0.16);

        // Colour by distance from centre + noise: lean on light-vs-deep coral
        // contrast (not transparency) to build depth.
        const noise = (rng() - 0.5) * 0.55;
        const t = Math.min(1, Math.max(0, distNorm + noise));
        let color: string;
        if (t < 0.2) color = palette.deeper;
        else if (t < 0.45) color = palette.deep;
        else if (t < 0.68) color = palette.mid;
        else if (t < 0.88) color = palette.light;
        else color = palette.pale;

        const opacity = 0.86 + (1 - distNorm) * 0.08 + (rng() - 0.5) * 0.06;
        cells.push({ px, py, rad, color, opacity, hexDist });
      }
    }

    // A few small infill discs sitting on top, close to the cluster, to break
    // up the regular hex grid (no far-flung outliers).
    const infillCount = 5;
    for (let i = 0; i < infillCount; i++) {
      const a = rng() * Math.PI * 2;
      const d = r0 * (0.4 + rng() * 0.9);
      const px = cx + Math.cos(a) * d;
      const py = cy + Math.sin(a) * d;
      const rad = r0 * (0.42 + rng() * 0.28);
      const t = rng();
      const color = t < 0.45 ? palette.pale : t < 0.8 ? palette.light : palette.mid;
      cells.push({ px, py, rad, color, opacity: 0.8 + rng() * 0.1, hexDist: -1 });
    }

    // Render order: bigger/outer first, small infill discs last (on top).
    cells.sort((a, b) => b.hexDist - a.hexDist);
    return cells;
  }, [rng, resolvedRings, radius, palette]);

  const S = size;
  const wrapperBg: React.CSSProperties = bgColor
    ? {
        background: bgColor,
        backgroundImage: `radial-gradient(140% 100% at 50% 55%, ${bgColor} 55%, #e8dec9 100%)`,
      }
    : {};

  return (
    <div
      className={className}
      aria-label="Coral bloom"
      style={{
        position: 'relative',
        width: S,
        height: S,
        overflow: 'hidden',
        ...wrapperBg,
      }}
    >
      {/* The bloom. A light wrapper blur softens edges without dissolving shape. */}
      <div style={{ position: 'absolute', inset: 0, filter: `blur(${blur}px)` }}>
        {circles.map((c, i) => {
          const px = c.px * S;
          const py = c.py * S;
          const pr = c.rad * S;
          // Pointy-top hex (matches the hex grid orientation). shapeMix eases
          // the polygon back toward a circle as it approaches 0.
          let clipPath: string | undefined;
          if (shape === 'hex') {
            const m = Math.max(0, Math.min(1, shapeMix));
            const ex = 50 * (0.866 + (1 - 0.866) * (1 - m));
            const ey = 50 * m;
            const pts: number[][] = [
              [50, 0],
              [50 + ex, 50 - ey],
              [50 + ex, 50 + ey],
              [50, 100],
              [50 - ex, 50 + ey],
              [50 - ex, 50 - ey],
            ];
            clipPath = `polygon(${pts.map(([x, y]) => `${x}% ${y}%`).join(', ')})`;
          }
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                left: px - pr,
                top: py - pr,
                width: pr * 2,
                height: pr * 2,
                borderRadius: '50%',
                background: `radial-gradient(circle at 50% 50%, ${c.color} 0%, ${c.color} 72%, ${hexWithAlpha(
                  c.color,
                  0
                )} 100%)`,
                opacity: c.opacity * opacityScale,
                clipPath,
              }}
            />
          );
        })}

        {/* Very soft outer halo bleeding into the surface behind. It sits inside
            the blurred layer so that layer paints its whole box: iOS Safari
            draws a magenta line at the edge of a blurred layer whose painted
            content stops short of it. */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background:
              'radial-gradient(55% 50% at 52% 56%, rgba(236,150,118,0.10), rgba(241,232,216,0) 75%)',
          }}
        />
      </div>
    </div>
  );
};

export default HexBloom;
